import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from './Button.js';
import { StateBlock } from './ScreenLayout.js';

/**
 * CANVAS BOUNDARY : le filet sous une surface custom (regle G23).
 *
 * Une surface qui sort du gabarit (App Canvas ou Space Canvas,
 * `docs/GABARIT-ECRAN.md` section 9) recoit les MEMES garanties qu'une vue du
 * socle : si elle plante, elle ne fait pas tomber l'ecran avec elle. C'est le
 * pendant de `ViewBoundary` (`packages/ui/src/views/ViewBoundary.tsx`) avec la
 * seule difference qui compte : une vue peut retomber sur `core.liste`, une
 * surface custom n'a AUCUN repli possible. Elle rend donc l'etat d'erreur
 * standard de la zone 5, celui du gabarit, jamais un ecran blanc.
 *
 * Le rail, l'en-tete et ce que le canvas a garde de la rangee 3 restent rendus
 * par le gabarit : ils survivent au plantage, donc l'utilisateur peut changer
 * de porte ou sortir de l'app sans recharger.
 *
 * Le filet se REARME quand `surfaceId` change (on quitte la porte fautive et
 * on y revient).
 */
export interface CanvasBoundaryProps {
  /**
   * Ce qui identifie la surface montee : l'id de la porte pour un Space
   * Canvas, l'id de l'app pour une App Canvas. Sert de cle de rearmement et
   * apparait dans l'avertissement console.
   */
  surfaceId: string;
  /**
   * Le titre de l'etat d'erreur. Defaut : la phrase du gabarit. Ecrit pour un
   * utilisateur, jamais pour un developpeur.
   */
  errorTitle?: string;
  /** La phrase sous le titre. Defaut : une invitation a recharger. */
  errorHint?: string;
  /**
   * REESSAYER SANS RECHARGER LA PAGE. Fourni, l'etat d'erreur porte un bouton
   * qui l'appelle puis remonte la surface. L'appelant y refait ce qui peut
   * l'etre : une surface chargee paresseusement RELANCE son import (`lazy()`
   * garde sinon l'echec d'un morceau rate jusqu'au F5, cf. `EcranAppCanvas`).
   */
  onRetry?: () => void;
  children: ReactNode;
}

interface CanvasBoundaryState {
  /** L'id de la surface qui a plante, null si tout va bien. */
  failedSurfaceId: string | null;
}

export class CanvasBoundary extends Component<CanvasBoundaryProps, CanvasBoundaryState> {
  override state: CanvasBoundaryState = { failedSurfaceId: null };

  static getDerivedStateFromProps(
    props: CanvasBoundaryProps,
    state: CanvasBoundaryState,
  ): CanvasBoundaryState | null {
    // Rearmement : on a change de surface depuis l'echec.
    if (state.failedSurfaceId != null && state.failedSurfaceId !== props.surfaceId) {
      return { failedSurfaceId: null };
    }
    return null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[taki/canvas] La surface ${this.props.surfaceId} a plante et a ete demontee.`,
      error,
      info.componentStack,
    );
    this.setState({ failedSurfaceId: this.props.surfaceId });
  }

  override render(): ReactNode {
    const { surfaceId, errorTitle, errorHint, onRetry, children } = this.props;
    if (this.state.failedSurfaceId === surfaceId) {
      return (
        <StateBlock
          role="alert"
          title={errorTitle ?? "Cette page n'a pas pu s'afficher"}
          hint={errorHint ?? 'Recharge la page ; si ça recommence, préviens ton administrateur.'}
          {...(onRetry !== undefined
            ? {
                action: (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // L'appelant d'abord (une surface neuve), le rearmement
                      // ensuite : le rendu suivant monte la surface neuve.
                      onRetry();
                      this.setState({ failedSurfaceId: null });
                    }}
                  >
                    Réessayer
                  </Button>
                ),
              }
            : {})}
        />
      );
    }
    return children;
  }
}
